import type { FleetGroup } from '#domain/FleetGroup.ts';
import type { Tour, TourStatus } from '#domain/Tour.ts';
import type { Vehicle } from '#domain/Vehicle.ts';

const TourStatusWeight: Record<NonNullable<TourStatus>, number> = {
    problem: 1,
    'in-transit': 2,
    planned: 3,
    open: 4,
    finished: 5,
};

export const formatTourSidebarName = (tour: Tour): string | undefined => {
    if (tour.firstStop?.name && tour.lastStop?.name && tour.searchName) {
        return `"${tour.searchName} (${tour.firstStop.name} -> ${tour.lastStop.name})"`;
    }
};

export const mapTourStatusToOrderStatusWeight = (status: TourStatus | undefined): number => {
    return status ? TourStatusWeight[status] : 5;
};

export type TourWithSortableKey = Tour & {
    statusWeight: number;
    firstStopEndTarget?: string;
    lastStopEndTarget?: string;
    progressPercentage?: number;
    licensePlate?: string;
    fleetGroupName?: string;
};

export const getVehicleFleetGroupName = (vehicle: Vehicle | undefined, fleetGroups: FleetGroup[] = []) => {
    if (!vehicle) {
        return undefined;
    }

    if (vehicle.fleetGroupName) {
        return vehicle.fleetGroupName;
    }

    if (!vehicle.fleetGroupIds?.length) {
        return undefined;
    }

    const fleetGroupNames = vehicle.fleetGroupIds
        .map(groupId => fleetGroups.find(group => group.id === groupId)?.name)
        .filter((groupName): groupName is string => typeof groupName === 'string' && groupName.trim() !== '');

    return fleetGroupNames.length > 0 ? fleetGroupNames.join(', ') : undefined;
};

// We need to add new keys only used for sorting the items (its not supported to do sorting on nested objects)
export const addSortableTourKey = (
    items: Tour[],
    vehicles: Vehicle[],
    fleetGroups: FleetGroup[] = []
): TourWithSortableKey[] =>
    items.map(tour => ({
        ...tour,
        firstStopEndTarget: tour.firstStop?.endTarget,
        lastStopEndTarget: tour.lastStop?.endTarget,
        progressPercentage:
            tour.progress.totalStopAmount > 0
                ? (tour.progress.finishedStopAmount / tour.progress.totalStopAmount) * 100
                : 0,
        licensePlate: getTourVehicle(tour, vehicles)?.plate,
        fleetGroupName: getVehicleFleetGroupName(getTourVehicle(tour, vehicles), fleetGroups),
        statusWeight: mapTourStatusToOrderStatusWeight(tour.status),
    }));

export const getTourVehicle = (tour: Tour, vehicles: Vehicle[]) =>
    vehicles.find(vehicle => vehicle.id === tour.truck.id);
